import React, { ReactElement, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';

import { createUrl, request } from 'app/utils';
import { Box, Flex, Loading, Page, Section, Typography } from 'components';
import { LastBadges } from 'features/badges';
import { useSchool } from 'features/school';
import { useBadges, StudentBadgesApiResponse } from 'features/badges';
import { useProducts, ProductsApiResponse, ProductsCompletedApiResponse, ProductsList } from 'features/products';
import styled from 'styled-components';
import { useMenu } from 'features/menu';

import usestorage from 'app/utils/storage';
import { useSearchParams } from 'react-router-dom';

const SquareLines = styled('i')`
  position: relative;
  position: absolute;
  left: 0;
  width: 0px;
  height: 0px;

  &::after {
    content: '';
    display: block;
    background-color: ${({ theme }) => theme.colors.primary_200};
    position: absolute;
    z-index: 1000;
  }

  &::before {
    content: '';
    display: block;

    border-top: 2px solid ${({ theme }) => theme.colors.white25};
    border-right: 2px solid ${({ theme }) => theme.colors.white25};
    position: absolute;

    right: 0px;
    z-index: 1;
  }

  ${({ theme: { mediaQueries } }) => {
    return `
    ${mediaQueries.mobile} {
      top: 1px;
      left: -25px;

      &::after {
        width: 14px;
        height: 14px;
      }

      &::before {
        top: -30px;
        width: 150px;
        height: 230px;
      }
    }

    ${mediaQueries.tablet} {
      left: -30px;

      &::after {
        width: 14px;
        height: 14px;
        top: 3px;
        left: 0px;
      }

      &::before {
        top: -40px;
        width: 100px;
        height: 150px;
      }
    }

    ${mediaQueries.desktop} {
      top: 4px;
      left: -40px;

      &::after {
        width: 17px;
        height: 17px;
        top: 0px;
        left: 0px;
      }

      &::before {
        top: -40px;
        width: 150px;
        height: 280px;
      }
    }
  `;
  }}
`;

const MyProductsView = (): ReactElement => {
  const { apiGatewayUrl, schoolUuid } = useSchool();
  const [loading, setLoading] = useState(true);
  const { setBackPathname, clearBackPathname } = useMenu();

  const { setStudentBadges, clearStudentBadges } = useBadges();

  const { productsMy, setProductsMy, clearProductsMy, productsCompleted, setProductsCompleted, clearProductsCompleted } = useProducts();

  const storage = usestorage('localStorage');
  const currentDashboard = storage.get('currentDashboard');

  const [searchParams] = useSearchParams();
  const currentDashboardArea = searchParams.get('dashboardAreaUuid');

  const fetch = async () => {
    await Promise.all([
      request({
        url: `/api/v1/School/StudentBadges`,
        method: 'post',
        baseURL: apiGatewayUrl,
        data: {
          productSlug: null,
          specialOnly: null,
          itensPerQuery: 8,
        },
      }).then((response: AxiosResponse<StudentBadgesApiResponse>) => {
        setStudentBadges(response.data);
      }),
      request({
        url: '/api/v1/School/MyNotCompletedCourses',
        method: 'get',
        baseURL: apiGatewayUrl,
        params: {
          ...(currentDashboard ? { dashboardUuid: currentDashboard } : {}),
          ...(currentDashboardArea ? { dashboardAreaUuid: currentDashboardArea } : {}),
        },
      }).then((response: AxiosResponse<ProductsApiResponse>) => {
        setProductsMy(response.data);
      }),
      request({
        url: '/api/v1/School/MyCompletedCourses',
        method: 'get',
        baseURL: apiGatewayUrl,
        params: {
          ...(currentDashboard ? { dashboardUuid: currentDashboard } : {}),
          ...(currentDashboardArea ? { dashboardAreaUuid: currentDashboardArea } : {}),
        },
      }).then((response: AxiosResponse<ProductsCompletedApiResponse>) => {
        setProductsCompleted(response.data);
      }),
    ]).then(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    fetch();

    setBackPathname(createUrl('dashboard'));

    return () => {
      clearStudentBadges();
      clearProductsMy();
      clearProductsCompleted();
      clearBackPathname();
    };
  }, []);

  const isBenndorf = schoolUuid === 'cfaf550b-037d-45b2-9f84-979fb01185d4';
  const isMeuPlanoCrypto = schoolUuid === '20b929ad-c773-46e2-93de-991988587835';

  let headTitle = 'Meus cursos';
  let currentTitle = 'Meus cursos atuais';
  let completedTitle = 'Meus cursos concluídos';

  if (isBenndorf) {
    headTitle = 'Meus produtos';
    currentTitle = 'Meus produtos atuais';
    completedTitle = 'Meus produtos concluídos';
  } else if (isMeuPlanoCrypto) {
    headTitle = 'Conteúdos';
    currentTitle = 'Conteúdos disponíveis';
    completedTitle = 'Conteúdos concluídos';
  }

  if (loading) return <Loading label="Por favor, aguarde." />;

  return (
    <Page title={headTitle}>
      <Flex flexGrow={0} bg="navbarBg">
        <Section flexDirection={{ m: 'column', d: 'row' }} justifyContent="space-between">
          <Flex flexGrow={0} flexDirection="column" pt={{ m: 40, d: 40 }} pb={{ m: 60, d: 100 }}>
            <Box position="relative">
              <SquareLines />
              <Typography type="type5" color="white" fontWeight="bold" upper>
                Todos os
              </Typography>
            </Box>

            <Typography type="type11" color="white" fontWeight="bold" mt={2}>
              {headTitle}
            </Typography>
          </Flex>

          <Flex flexGrow={0} bg="green" position="relative" mb={{ m: 30, d: 0 }}>
            <Box position={{ d: 'absolute' }} right={{ d: 0 }}>
              <LastBadges />
            </Box>
          </Flex>
        </Section>
      </Flex>

      <Flex py={{ m: 30, t: 60, d: 90 }} flexGrow={1} flexDirection="column">
        <Flex bg="gray_200" flexGrow={0}>
          <Section flexDirection="column">
            <Flex justifyContent="space-between" flexWrap={{ m: 'wrap', d: 'nowrap' }} alignItems="flex-end">
              <Flex flexDirection="column">
                <Typography as="h2" type="type8" fontWeight="bold" mt={1}>
                  {currentTitle}
                </Typography>
              </Flex>
            </Flex>

            <ProductsList products={productsMy} active />
          </Section>
        </Flex>

        {Boolean(productsCompleted && productsCompleted.length) && (
          <Flex pt={{ m: 30, t: 60, d: 90 }} bg="gray_200" flexGrow={0}>
            <Section flexDirection="column">
              <Flex justifyContent="space-between" flexWrap={{ m: 'wrap', d: 'nowrap' }} alignItems="flex-end">
                <Flex flexDirection="column">
                  <Typography as="h2" type="type8" fontWeight="bold" mt={1}>
                    {completedTitle}
                  </Typography>
                </Flex>
              </Flex>

              <ProductsList products={productsCompleted} completed />
            </Section>
          </Flex>
        )}
      </Flex>
    </Page>
  );
};

export default MyProductsView;
